// node modules
import React from 'react';
import { observer } from 'mobx-react';
import dayjs from 'dayjs';
// modules
import FormatService from 'modules/Format';
import { useStore } from 'modules/Stores';
import Validation from 'modules/Validation';
// ui library
import {
  View,
  Text,
  Radio,
  DropDown,
  DropdownButton,
  useToggle,
} from '@upstox/upstox-ui';
// utils
import { getSelectedExpiry } from 'modules/OptionChain/utils';
import { decomposeExchangeToken } from 'modules/BOD/modules/Search/utils';
// styles
import * as css from './styles';

const ExpiryDropdown: React.FC = () => {
  const {
    expiries,
    selectedExpiry,
    setSelectedExpiry,
    isMinimised,
    scripData,
  } = useStore('OptionChainStore');

  const [expiryOpened, toggleExpiryOpened] = useToggle(false);

  const handleSelectExpiry = (variant: number) => {
    setSelectedExpiry(variant);
    toggleExpiryOpened();
  };

  const { segment } = decomposeExchangeToken(scripData?.exchangeToken || '');

  const isCommodity = Validation.isCommodity(segment);

  const selectedExpiryMonth = getSelectedExpiry(
    expiries,
    selectedExpiry || 0,
    isCommodity,
  );

  const checkExpiry = (timestamp: number) => {
      // we do this because for commodities future and option expiration date are different
    if (isCommodity) {
      const selectedExpiryMonthValue = dayjs(selectedExpiryMonth).month() + 1;
      const expiryMonth = dayjs(timestamp).month() + 1;
      return expiryMonth === selectedExpiryMonthValue;
    }
    return timestamp === selectedExpiry;
  };

  return (
    <DropDown
      alignment="left"
      onOpen={toggleExpiryOpened}
      onClose={toggleExpiryOpened}
      initialOpen={false}
      isOpen={expiryOpened}
      renderTrigger={({ onOpen, onClose, open, ...otherProps }) => (
        <DropdownButton
          data-qa="expiry-date"
          text={
            selectedExpiryMonth
              ? FormatService.formatDate(selectedExpiryMonth, 'DD_MMMM_YY')
              : '--'
          }
          isOpen={open}
          onClick={open ? onClose : onOpen}
          css={css.dropdownButton}
          disabled={isMinimised}
          textFont="tvFont"
          {...otherProps}
        />
      )}
    >
      <View flexDirection="column" css={css.dropdownMenu}>
        {expiries.map(timestamp => (
          <View
            key={timestamp}
            alignItems="center"
            css={css.dropdownOption}
            onClick={() => handleSelectExpiry(timestamp)}
            gap="xsmall"
          >
            <Radio readOnly checked={checkExpiry(timestamp)} />

            <Text variant="heading3" family="tvFont">
              {FormatService.formatDate(timestamp, 'DD_MMM_YYYY')}
            </Text>
          </View>
        ))}
      </View>
    </DropDown>
  );
};

export default observer(ExpiryDropdown);
